import type { Project, ProjectAttention, ProjectProgress } from "./entities";

export const PROJECT_QUIET_GRACE_DAYS = 14;

type AttentionProject = Pick<Project, "status" | "archivedAt" | "completedAt" | "lastActivityAt">;
type AttentionProgress = Pick<ProjectProgress, "open" | "completed" | "cancelled">;

function dateKey(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" })
    .formatToParts(date)
    .filter((part) => part.type !== "literal");
  const read = (type: string) => parts.find((part) => part.type === type)?.value;
  return `${read("year")}-${read("month")}-${read("day")}`;
}

function addDays(date: string, amount: number): string {
  const value = new Date(`${date}T12:00:00Z`);
  value.setUTCDate(value.getUTCDate() + amount);
  return value.toISOString().slice(0, 10);
}

export function projectAttention(
  project: AttentionProject,
  progress: AttentionProgress,
  timeZone: string,
  now: Date = new Date(),
): ProjectAttention {
  if (project.status === "archived" || project.archivedAt) return "archived";
  if (project.status === "someday") return "someday";
  if (progress.open > 0) return "active";
  const hasHistory = progress.completed + progress.cancelled > 0 || Boolean(project.completedAt);
  const lastActivity = dateKey(new Date(project.lastActivityAt), timeZone);
  const currentDate = dateKey(now, timeZone);
  const graceEnd = addDays(lastActivity, PROJECT_QUIET_GRACE_DAYS - 1);
  return currentDate <= graceEnd ? (hasHistory ? "recently_finished" : "starting") : "quiet";
}

export function projectAttentionLabel(attention: ProjectAttention): string {
  return ({ starting: "Starting", active: "Active", recently_finished: "Recently finished", quiet: "Quiet", someday: "Someday", archived: "Archived" } as Record<ProjectAttention, string>)[attention];
}

export function projectAttentionDescription(attention: ProjectAttention): string {
  return ({
    starting: "No Tasks yet; this Project stays visible while it is new.",
    active: "At least one Open Task keeps this Project in attention.",
    recently_finished: "No Open Tasks remain; this Project stays recently finished for 14 days.",
    quiet: "No Open Tasks remain and the 14-day attention window has ended.",
    someday: "Paused until you resume it from Someday.",
    archived: "Stored as history and excluded from attention and review surfaces.",
  } as Record<ProjectAttention, string>)[attention];
}

export function projectAttentionRank(attention: ProjectAttention): number {
  return ({ starting: 0, active: 1, recently_finished: 2, quiet: 3, someday: 4, archived: 5 } as Record<ProjectAttention, number>)[attention];
}
